import { registrationCategory } from "../constants/register.constants";
import {
  verifyUserType,
  verifyUserTypeConnectedRegistrar,
} from "../shared/helpers";
import {
  getUserCapabilities,
  hasPermission,
  userCanAccess,
} from "../shared/permissions";
import { FEATURES } from "../constants/permissions.constants";
import { useAuth } from "../contexts/AuthContext";

/**
 * User/roles from AuthContext (set only after AuthProvider validates session).
 * Permissions come from GET /users/profile and are refreshed on session validation.
 */
const useRoles = () => {
  const { user } = useAuth();
  const storageData = user ?? null;

  const isPharmacy = verifyUserType(registrationCategory.PHARMACY, storageData);
  const isConnectedNurse = verifyUserType(
    registrationCategory.CONNECTED_NURSE,
    storageData
  );
  const isPrescriber = verifyUserType(
    registrationCategory.PRESCRIBER,
    storageData
  );
  const isHomecare = verifyUserType(
    registrationCategory.CONNECTED_HOMECARE,
    storageData
  );
  const isOutpatient = verifyUserType(
    registrationCategory.CONNECTED_OUTPATIENT,
    storageData
  );
  const isCustomerSolution = verifyUserType(
    registrationCategory.CUSTOMER_SOLUTION,
    storageData
  );
  const isConnectedRegistrar = verifyUserTypeConnectedRegistrar(
    registrationCategory.CONNECTED_REGISTRAR,
    storageData
  );

  const isHcp =
    isPrescriber ||
    isPharmacy ||
    isConnectedNurse ||
    isHomecare ||
    isOutpatient;

  const isPrimaryPharmacist = Boolean(storageData?.primary_user);
  const capabilities = getUserCapabilities(storageData);

  /** Customer care cannot access Manage Patients. */
  const canManagePatients = !isCustomerSolution && isHcp;

  return {
    isConnectedNurse,
    isConnectedRegistrar,
    isPharmacy,
    isPrescriber,
    isHomecare,
    isCustomerSolution,
    isOutpatient,
    isHcp,
    storageData,
    isPrimaryPharmacist,
    canManagePatients,
    capabilities,
    canViewReporting: capabilities.reporting,
    canManageUsers: capabilities.userManagement,
    canChangeUser: capabilities.userManagementChange,
    canManageUserMedication: capabilities.userManagementMedication,
    canAddUser: capabilities.userManagementAdd,
    canDeleteUser: capabilities.userManagementDelete,
    canViewHospitals: capabilities.hospitalsView,
    canViewAuditTrail: capabilities.auditTrail,
    canReviewPaf: capabilities.pafReview,
    canViewReviewOutcomeOptions: capabilities.reviewOutcomeOptions,
    canChangePrescription: capabilities.pafEdit,
    hasPermission: (permission) => hasPermission(storageData, permission),
    canAccess: (feature) => userCanAccess(storageData, feature),
    FEATURES,
  };
};

export default useRoles;
